import { useEffect, useState, useMemo, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import useGa4Property from "../hooks/useGa4Property"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import { normalizeValue } from "../utils/normalizeValue"
import { normalizeEnhancedMeasurementSettings } from "../utils/ga4EnhancedMeasurement"
import { getValidAccessToken, isGa4EditTokenValid } from "../utils/ga4Tokens"
import useGa4EditAuthorization from "../hooks/useGa4EditAuthorization"

export const INDUSTRY_CATEGORIES = [
  "INDUSTRY_CATEGORY_UNSPECIFIED",
  "AUTOMOTIVE",
  "BUSINESS_AND_INDUSTRIAL_MARKETS",
  "FINANCE",
  "FOOD_AND_DRINK",
  "GAMES",
  "HEALTH",
  "HOBBIES_AND_LEISURE",
  "HOUSE_AND_HOME",
  "INTERNET_AND_TELECOM",
  "LAW_AND_GOVERNMENT",
  "NEWS",
  "ONLINE_COMMUNITIES",
  "PEOPLE_AND_SOCIETY",
  "PETS_AND_ANIMALS",
  "REAL_ESTATE",
  "REFERENCE",
  "SCIENCE",
  "SHOPPING",
  "SPORTS",
  "TRAVEL",
  "OTHER",
]

export const DATA_RETENTION_OPTIONS = ["TWO_MONTHS", "FOURTEEN_MONTHS"]
export const GOOGLE_SIGNALS_STATES = [
  "GOOGLE_SIGNALS_ENABLED",
  "GOOGLE_SIGNALS_DISABLED",
]

const ENHANCED_MEASUREMENT_FIELDS = [
  { key: "streamEnabled", label: "Stream enabled" },
  { key: "scrollsEnabled", label: "Scrolls" },
  { key: "outboundClicksEnabled", label: "Outbound clicks" },
  { key: "siteSearchEnabled", label: "Site search" },
  { key: "videoEngagementEnabled", label: "Video engagement" },
  { key: "fileDownloadsEnabled", label: "File downloads" },
  { key: "pageChangesEnabled", label: "Page changes" },
  { key: "formInteractionsEnabled", label: "Form interactions" },
]

const EMPTY_TEMPLATE_EXPECTATION = Object.freeze({
  hasExpectation: false,
  settings: null,
})

const INITIAL_FORM_VALUES = Object.freeze({
  timeZone: "",
  currencyCode: "",
  industryCategory: "",
  userDataRetention: "",
  eventDataRetention: "",
  googleSignalsState: "",
  resetUserDataOnNewActivity: "",
  enhancedMeasurementSettings: null,
})

const isValidHttpUrl = (value) => {
  if (typeof value !== "string") return false
  const trimmed = value.trim()
  if (!trimmed) return false
  try {
    const url = new URL(trimmed)
    if (!url.hostname) return false
    return url.protocol === "http:" || url.protocol === "https:"
  } catch (err) {
    return false
  }
}

export default function GA4PropertySettings() {
  const user = useUser()
  const navigate = useNavigate()
  const { search } = useLocation()
  const propertyId = new URLSearchParams(search).get("id")
  const [report, setReport] = useState({})
  const [reportLoading, setReportLoading] = useState(true)
  const [reportError, setReportError] = useState(null)
  const [settings, setSettings] = useState({})
  const [dataStreams, setDataStreams] = useState([])
  const [editing, setEditing] = useState(false)
  const [formValues, setFormValues] = useState(() => ({
    ...INITIAL_FORM_VALUES,
  }))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [settingsLoadedOnce, setSettingsLoadedOnce] = useState(false)
  const [templateLoading, setTemplateLoading] = useState(false)
  const [templateLoadedOnce, setTemplateLoadedOnce] = useState(false)
  const [templateEnhancedExpectation, setTemplateEnhancedExpectation] =
    useState(() => ({
      ...EMPTY_TEMPLATE_EXPECTATION,
    }))
  const lastTemplateIdRef = useRef(null)
  const {
    property: propInfo,
    loading: propertyLoading,
    error: propertyError,
    refresh: refreshProperty,
  } = useGa4Property(propertyId)

  useEffect(() => {
    setSettings({})
    setFormValues({ ...INITIAL_FORM_VALUES })
    setDataStreams([])
    setSettingsLoadedOnce(false)
    setTemplateLoadedOnce(false)
    setTemplateEnhancedExpectation({ ...EMPTY_TEMPLATE_EXPECTATION })
    lastTemplateIdRef.current = null
  }, [propertyId])

  const {
    editTokenRecord,
    editAuthPending,
    editAuthMessage,
    editAuthError,
    editAuthUrl,
    editPopupBlocked,
    resetAuthorizationState: resetEditAuthState,
    refreshTokenFromStorage,
    startEditAuthFlow,
    ensureEditToken,
    handleEditAuthFailure,
  } = useGa4EditAuthorization({ supabaseClient: supabase, user })

  const normalizedId = propertyId?.replace(/^properties\//, "")

  const getToken = async () => await getValidAccessToken(supabase, user)
  const tzOptions = useMemo(
    () =>
      typeof Intl.supportedValuesOf === "function"
        ? Intl.supportedValuesOf("timeZone")
        : [],
    []
  )
  const currencyOptions = useMemo(
    () =>
      typeof Intl.supportedValuesOf === "function"
        ? Intl.supportedValuesOf("currency")
        : [],
    []
  )

  useEffect(() => {
    if (!propertyId) return

    const templateId = propInfo?.template_id ?? null
    if (!templateId) {
      setTemplateEnhancedExpectation({ ...EMPTY_TEMPLATE_EXPECTATION })
      setTemplateLoading(false)
      setTemplateLoadedOnce(true)
      lastTemplateIdRef.current = null
      return
    }

    if (lastTemplateIdRef.current === templateId && templateLoadedOnce) {
      return
    }

    lastTemplateIdRef.current = templateId

    let cancelled = false
    setTemplateLoadedOnce(false)
    setTemplateLoading(true)

    const loadTemplate = async () => {
      try {
        const { data, error } = await supabase
          .from("ga4_templates")
          .select("template")
          .eq("id", templateId)
          .maybeSingle()
        if (error) throw error
        if (cancelled) return
        const tpl = data?.template
        if (tpl && typeof tpl === "object") {
          if (
            Object.prototype.hasOwnProperty.call(
              tpl,
              "enhancedMeasurementSettings"
            )
          ) {
            const raw = tpl.enhancedMeasurementSettings
            if (raw && typeof raw === "object") {
              setTemplateEnhancedExpectation({
                hasExpectation: true,
                settings: normalizeEnhancedMeasurementSettings(raw, {
                  includeDefaults: true,
                }),
              })
            } else if (raw === null) {
              setTemplateEnhancedExpectation({
                hasExpectation: true,
                settings: null,
              })
            } else {
              setTemplateEnhancedExpectation({
                ...EMPTY_TEMPLATE_EXPECTATION,
              })
            }
          } else {
            setTemplateEnhancedExpectation({
              ...EMPTY_TEMPLATE_EXPECTATION,
            })
          }
        } else {
          setTemplateEnhancedExpectation({
            ...EMPTY_TEMPLATE_EXPECTATION,
          })
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load GA4 template expectation", err)
          setTemplateEnhancedExpectation({ ...EMPTY_TEMPLATE_EXPECTATION })
        }
      } finally {
        if (!cancelled) {
          setTemplateLoading(false)
          setTemplateLoadedOnce(true)
        }
      }
    }

    loadTemplate()

    return () => {
      cancelled = true
    }
  }, [propertyId, propInfo?.template_id, templateLoadedOnce])

  const loading =
    propertyLoading ||
    reportLoading ||
    (!settingsLoadedOnce && settingsLoading) ||
    (!templateLoadedOnce && templateLoading)

  useEffect(() => {
    if (user && propertyId) {
      fetchSettings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, propertyId])

  useEffect(() => {
    if (propertyLoading) return

    if (propertyError || !propInfo?.id) {
      setReport({})
      setReportError(null)
      setReportLoading(false)
      return
    }

    let cancelled = false

    const loadReport = async () => {
      setReportLoading(true)
      setReportError(null)
      try {
        const { data: cmp, error } = await supabase
          .from("ga4_property_template_mismatches")
          .select("mismatches")
          .eq("ga4_property_id", propInfo.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle()
        if (error) throw error
        if (cancelled) return
        setReport(cmp?.mismatches || {})
      } catch (err) {
        if (cancelled) return
        console.error("Failed to load GA4 property mismatch report", err)
        setReport({})
        setReportError(err?.message || "Failed to load mismatch report")
      } finally {
        if (!cancelled) setReportLoading(false)
      }
    }

    loadReport()

    return () => {
      cancelled = true
    }
  }, [propInfo?.id, propertyLoading, propertyError])

  const fetchSettings = async () => {
    setError(null)
    setSettingsLoading(true)
    if (!settingsLoadedOnce) {
      setSettings({})
      setFormValues({ ...INITIAL_FORM_VALUES })
      setDataStreams([])
    }

    try {
      const accessToken = await getToken()
      if (!accessToken) {
        setError("Missing GA4 access token")
        return
      }

      const res = await supabase.functions.invoke("ga4-proxy", {
        body: { type: "settings-audit", propertyId: normalizedId },
        headers: { "x-ga4-authorization": `Bearer ${accessToken}` },
      })

      if (res.error) {
        const msg =
          res.error.context?.body?.error?.message || res.error.message || "Error"
        setError(msg)
        return
      }

      const data = res.data || {}
      const rawStreams = Array.isArray(data?.dataStreams?.dataStreams)
        ? data.dataStreams.dataStreams
        : []
      const enhancedEntries = Array.isArray(data?.enhancedMeasurementSettings)
        ? data.enhancedMeasurementSettings
        : []
      const combinedStreams = rawStreams
        .filter((stream) => stream && typeof stream === "object")
        .map((stream) => ({ stream }))
      const firstEnhancedEntry = enhancedEntries.find(
        (entry) =>
          entry &&
          typeof entry === "object" &&
          entry.settings &&
          typeof entry.settings === "object"
      )
      const normalizedEnhanced = normalizeEnhancedMeasurementSettings(
        firstEnhancedEntry?.settings,
        { includeDefaults: false }
      )
      const s = {
        timeZone: data.timeZone || "",
        currencyCode: data.currencyCode || "",
        industryCategory: data.industryCategory || "",
        userDataRetention:
          data.dataRetentionSettings?.userDataRetention || "",
        eventDataRetention:
          data.dataRetentionSettings?.eventDataRetention || "",
        resetUserDataOnNewActivity:
          data.dataRetentionSettings?.resetUserDataOnNewActivity ?? "",
        googleSignalsState: data.googleSignalsSettings?.state || "",
        enhancedMeasurementSettings: normalizedEnhanced,
      }
      setSettings(s)
      setFormValues(s)
      setDataStreams(combinedStreams)
    } catch (err) {
      console.error("Failed to load GA4 property settings", err)
      setError(err?.message || "Failed to load GA4 settings")
    } finally {
      setSettingsLoading(false)
      setSettingsLoadedOnce(true)
    }
  }

  const startAdjust = async () => {
    resetEditAuthState()
    refreshTokenFromStorage()
    const editToken = await ensureEditToken()
    if (editToken) {
      await fetchSettings()
    }
    setEditing(true)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormValues((prev) => ({ ...prev, [name]: value }))
  }

  const handleSave = async (e) => {
    e?.preventDefault()
    setSaving(true)
    setError(null)
    const editToken = await ensureEditToken()
    if (!editToken) {
      setSaving(false)
      return
    }
    const propPayload = {
      timeZone: formValues.timeZone,
      currencyCode: formValues.currencyCode,
      industryCategory: formValues.industryCategory,
    }
    const propMask = Object.keys(propPayload).join(",")
    const drPayload = {
      userDataRetention: formValues.userDataRetention,
      eventDataRetention: formValues.eventDataRetention,
      resetUserDataOnNewActivity:
        formValues.resetUserDataOnNewActivity === "true",
    }
    const drMask = Object.keys(drPayload).join(",")
    const gsPayload = { state: formValues.googleSignalsState }
    const gsMask = Object.keys(gsPayload).join(",")

    const updates = [
      { type: "update-property", payload: propPayload, updateMask: propMask },
      {
        type: "update-data-retention",
        payload: drPayload,
        updateMask: drMask,
      },
      {
        type: "update-google-signals",
        payload: gsPayload,
        updateMask: gsMask,
      },
    ]

    for (const u of updates) {
      const { error: err } = await supabase.functions.invoke("ga4-proxy", {
        body: { propertyId: normalizedId, ...u },
        headers: { "x-ga4-authorization": `Bearer ${editToken}` },
      })
      if (err) {
        const msg = err.context?.body?.error?.message || err.message || "Error"
        const status = err.context?.status
        const normalizedMsg = (msg || "").toLowerCase()
        if (
          status === 401 ||
          status === 403 ||
          normalizedMsg.includes("permission") ||
          normalizedMsg.includes("insufficient")
        ) {
          await handleEditAuthFailure(msg)
        }
        setError(msg)
        setSaving(false)
        return
      }
    }
    resetEditAuthState()
    setSaving(false)
    setEditing(false)
    fetchSettings()
    refreshProperty()
  }

  const renderSettingsTable = () => {
    const allKeys = Array.from(
      new Set([
        ...Object.keys(settings || {}),
        ...Object.keys(report.settings || {}),
      ])
    )
    const filteredKeys = allKeys.filter(
      (key) => key !== "enhancedMeasurementSettings"
    )
    if (!filteredKeys.length) return <p>No GA4 settings compared.</p>
    const editable = new Set([
      "timeZone",
      "currencyCode",
      "industryCategory",
      "userDataRetention",
      "eventDataRetention",
      "googleSignalsState",
      "resetUserDataOnNewActivity",
    ])
    const renderInput = (key) => {
      switch (key) {
        case "timeZone":
          return tzOptions.length ? (
            <select name="timeZone" value={formValues.timeZone} onChange={handleChange}>
              {tzOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          ) : (
            <input name="timeZone" value={formValues.timeZone} onChange={handleChange} />
          )
        case "currencyCode":
          return currencyOptions.length ? (
            <select
              name="currencyCode"
              value={formValues.currencyCode}
              onChange={handleChange}
            >
              {currencyOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          ) : (
            <input
              name="currencyCode"
              value={formValues.currencyCode}
              onChange={handleChange}
            />
          )
        case "industryCategory":
          return (
            <select
              name="industryCategory"
              value={formValues.industryCategory}
              onChange={handleChange}
            >
              {INDUSTRY_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          )
        case "userDataRetention":
        case "eventDataRetention":
          return (
            <select
              name={key}
              value={formValues[key]}
              onChange={handleChange}
            >
              {DATA_RETENTION_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          )
        case "googleSignalsState":
          return (
            <select
              name="googleSignalsState"
              value={formValues.googleSignalsState}
              onChange={handleChange}
            >
              {GOOGLE_SIGNALS_STATES.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          )
        case "resetUserDataOnNewActivity":
          return (
            <select
              name="resetUserDataOnNewActivity"
              value={formValues.resetUserDataOnNewActivity}
              onChange={handleChange}
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          )
        default:
          return JSON.stringify(normalizeValue(formValues[key]))
      }
    }
    const formatObjectCellValue = (value) => {
      if (value === undefined || value === null || value === "") return ""
      const normalized = normalizeValue(value)
      if (normalized === undefined || normalized === null || normalized === "")
        return ""
      if (Array.isArray(normalized)) {
        return JSON.stringify(normalized)
      }
      if (typeof normalized === "boolean") return normalized ? "true" : "false"
      if (typeof normalized === "number") return String(normalized)
      if (typeof normalized === "object") {
        try {
          return JSON.stringify(normalized)
        } catch (err) {
          return String(normalized)
        }
      }
      return String(normalized)
    }
    const renderObjectTable = (obj) => {
      if (!obj || typeof obj !== "object" || Array.isArray(obj)) return ""
      const entries = Object.entries(obj).filter(
        ([, val]) => val !== undefined
      )
      if (!entries.length) return ""
      return (
        <table className="ui-table"
          style={{
            borderCollapse: "collapse",
            width: "100%",
            maxWidth: "480px",
            backgroundColor: "rgba(15, 23, 42, 0.05)",
            borderRadius: "var(--border-radius)",
            overflow: "hidden",
          }}
        >
          <tbody>
            {entries.map(([subKey, subVal]) => (
              <tr key={subKey}>
                <th
                  style={{
                    textAlign: "left",
                    padding: "0.25rem 0.5rem",
                    whiteSpace: "nowrap",
                    verticalAlign: "top",
                    fontWeight: 600,
                  }}
                >
                  {subKey}
                </th>
                <td style={{ padding: "0.25rem 0.5rem" }}>
                  {formatObjectCellValue(subVal)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    }
    const formatDisplayValue = (
      key,
      value,
      { expected: isExpected = false } = {}
    ) => {
      if (value === undefined || value === null || value === "") return ""
      if (key === "enhancedMeasurementSettings") {
        const normalized = normalizeEnhancedMeasurementSettings(value, {
          includeDefaults: isExpected,
        })
        if (!normalized) return ""
        return renderObjectTable(normalized)
      }
      const normalized = normalizeValue(value)
      if (
        normalized &&
        typeof normalized === "object" &&
        !Array.isArray(normalized)
      ) {
        const table = renderObjectTable(normalized)
        return table || ""
      }
      if (Array.isArray(normalized)) {
        return (
          <code
            style={{
              display: "inline-block",
              backgroundColor: "rgba(15, 23, 42, 0.05)",
              padding: "0.25rem 0.5rem",
              borderRadius: "var(--border-radius)",
            }}
          >
            {JSON.stringify(normalized)}
          </code>
        )
      }
      if (typeof normalized === "boolean") return normalized ? "true" : "false"
      return String(normalized)
    }
    const serializeComparableValue = (
      key,
      value,
      { expected: isExpected = false } = {}
    ) => {
      if (value === undefined) return undefined
      if (value === null) return JSON.stringify(null)
      if (key === "enhancedMeasurementSettings") {
        const normalized = normalizeEnhancedMeasurementSettings(value, {
          includeDefaults: isExpected,
        })
        return normalized ? JSON.stringify(normalized) : JSON.stringify(null)
      }
      try {
        return JSON.stringify(normalizeValue(value))
      } catch (err) {
        return String(value)
      }
    }
    const hasValidEditToken = isGa4EditTokenValid(editTokenRecord)
    return (
      <div>
        <h3>Settings Comparison</h3>
        {error && editing && <p style={{ color: "red" }}>{error}</p>}
        <table className="ui-table">
          <thead>
            <tr>
              <th>Setting name</th>
              <th>GA4 setting</th>
              <th>Template setting</th>
              <th>Matches?</th>
            </tr>
          </thead>
          <tbody>
            {filteredKeys.map((key) => {
              const actual =
                settings[key] !== undefined
                  ? settings[key]
                  : report.settings?.[key]?.actual
              const expected = report.settings?.[key]?.expected
              const displayVal =
                editing && editable.has(key) ? formValues[key] : actual
              const hasExpected = expected !== undefined
              const actualComparable = serializeComparableValue(key, displayVal, {
                expected: false,
              })
              const expectedComparable = hasExpected
                ? serializeComparableValue(key, expected, { expected: true })
                : undefined
              const match =
                hasExpected && expectedComparable !== undefined
                  ? actualComparable === expectedComparable
                  : null
              const actualDisplay =
                editing && editable.has(key)
                  ? renderInput(key)
                  : formatDisplayValue(key, displayVal, { expected: false })
              const expectedDisplay = hasExpected
                ? formatDisplayValue(key, expected, { expected: true })
                : ""
              return (
                <tr key={key}>
                  <td>{key}</td>
                  <td>{actualDisplay}</td>
                  <td>{expectedDisplay}</td>
                  <td>{match == null ? "" : match ? "Yes" : "No"}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {editing && (
          <div style={{ marginTop: "0.5rem" }}>
            <div style={{ marginBottom: "0.75rem" }}>
              <div>
                <strong>GA4 edit access:</strong>{" "}
                <span
                  style={{
                    color: hasValidEditToken ? "#047857" : "#b45309",
                  }}
                >
                  {hasValidEditToken
                    ? "Authorized"
                    : "Authorization required"}
                </span>
              </div>
              {!hasValidEditToken && (
                <div style={{ marginTop: "0.5rem" }}>
                  <button
                    type="button"
                    onClick={startEditAuthFlow}
                    disabled={saving || editAuthPending}
                  >
                    Authorize GA4 Edits
                  </button>
                </div>
              )}
              {editAuthMessage && (
                <p style={{ marginTop: "0.5rem", color: "#444" }}>
                  {editAuthMessage}
                </p>
              )}
              {editPopupBlocked && editAuthUrl && (
                <p style={{ marginTop: "0.5rem", color: "#b45309" }}>
                  Pop-up blocked. {" "}
                  <a href={editAuthUrl} target="_blank" rel="noreferrer">
                    Open the Google authorization flow
                  </a>
                  .
                </p>
              )}
              {editAuthError && (
                <p style={{ marginTop: "0.5rem", color: "red" }}>
                  {editAuthError}
                </p>
              )}
            </div>
            <button onClick={handleSave} disabled={saving}>
              Save
            </button>
            <button
              type="button"
              onClick={() => {
                resetEditAuthState()
                setEditing(false)
                setFormValues(settings)
              }}
              disabled={saving}
              style={{ marginLeft: "0.5rem" }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    )
  }

  const renderEnhancedMeasurementSection = () => {
    const reportEntry = report.settings?.enhancedMeasurementSettings
    const hasReportActual = Object.prototype.hasOwnProperty.call(
      reportEntry || {},
      "actual"
    )
    const hasReportExpected = Object.prototype.hasOwnProperty.call(
      reportEntry || {},
      "expected"
    )
    const actualRaw = hasReportActual
      ? reportEntry.actual
      : settings.enhancedMeasurementSettings
    const expectedRawFromReport = hasReportExpected ? reportEntry.expected : null
    const normalizeForDisplay = (value) => {
      if (!value || typeof value !== "object") return null
      return normalizeEnhancedMeasurementSettings(value, {
        includeDefaults: true,
      })
    }
    const actualNormalized = normalizeForDisplay(actualRaw)
    const expectedNormalizedFromReport = hasReportExpected
      ? normalizeForDisplay(expectedRawFromReport)
      : null
    const expectedNormalized = hasReportExpected
      ? expectedNormalizedFromReport
      : templateEnhancedExpectation.hasExpectation
        ? templateEnhancedExpectation.settings
        : null
    const expectedAvailable =
      hasReportExpected || templateEnhancedExpectation.hasExpectation
    const hasActual = Boolean(actualNormalized)
    if (!hasActual && !expectedAvailable) return null

    const formatValue = (value) => {
      if (value === null || value === undefined || value === "") return "—"
      if (typeof value === "boolean") return value ? "true" : "false"
      if (Array.isArray(value)) return value.join(",")
      return String(value)
    }

    const rows = []
    rows.push({
      key: "mode",
      label: "Mode",
      actual: hasActual ? "Manage" : "Ignore",
      expected: expectedAvailable
        ? expectedNormalized
          ? "Manage"
          : "Ignore"
        : "—",
      match: expectedAvailable
        ? hasActual === Boolean(expectedNormalized)
        : null,
    })

    const hasExpectedSettings = expectedAvailable && Boolean(expectedNormalized)
    if (hasActual || hasExpectedSettings) {
      for (const { key, label } of ENHANCED_MEASUREMENT_FIELDS) {
        const actualValue = actualNormalized?.[key]
        const expectedValue = expectedNormalized?.[key]
        rows.push({
          key,
          label,
          actual: actualValue,
          expected: expectedValue,
          match: hasExpectedSettings
            ? actualValue === expectedValue
            : null,
        })
      }
      const actualQuery = actualNormalized?.searchQueryParameter ?? null
      const expectedQuery = expectedNormalized?.searchQueryParameter ?? null
      rows.push({
        key: "searchQueryParameter",
        label: "Search query parameters",
        actual: actualQuery,
        expected: expectedQuery,
        match: hasExpectedSettings
          ? (actualQuery || "") === (expectedQuery || "")
          : null,
      })
    }

    return (
      <div style={{ marginTop: "1.5rem" }}>
        <h3>Enhanced Measurement Settings</h3>
        <table className="ui-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Actual</th>
              <th>Template</th>
              <th>Matches?</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td>{formatValue(row.actual)}</td>
                <td>{formatValue(row.expected)}</td>
                <td>
      {row.match == null ? "" : row.match ? "Yes" : "No"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!hasActual && (
          <p style={{ marginTop: "0.75rem", color: "#6b7280" }}>
            Enhanced measurement settings were not available for this property.
          </p>
        )}
      </div>
    )
  }

  const renderDataStreamsSection = () => {
    if (!dataStreams.length) return null

    const renderInfoRow = (label, value) => {
      if (value === undefined || value === null || value === "") return null
      let content = value
      if (typeof value === "boolean") content = value ? "true" : "false"
      if (typeof value === "number") content = String(value)
      const isObject = value && typeof value === "object"
      const renderStreamObjectTable = (obj) => {
        if (!obj || typeof obj !== "object") return null
        const normalized = normalizeValue(obj)
        if (!normalized || typeof normalized !== "object") return null
        const entries = Object.entries(normalized).filter(
          ([key]) => key !== "name"
        )
        if (!entries.length) return null
        return (
          <table className="ui-table"
            style={{
              borderCollapse: "collapse",
              width: "100%",
              maxWidth: "520px",
              backgroundColor: "rgba(15, 23, 42, 0.05)",
              borderRadius: "var(--border-radius)",
              overflow: "hidden",
            }}
          >
            <tbody>
              {entries.map(([subKey, subVal]) => (
                <tr key={subKey}>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "0.25rem 0.5rem",
                      whiteSpace: "nowrap",
                      verticalAlign: "top",
                    }}
                  >
                    {subKey}
                  </th>
                  <td style={{ padding: "0.25rem 0.5rem" }}>
                    {(() => {
                      const normalizedValue = normalizeValue(subVal)
                      if (
                        normalizedValue === undefined ||
                        normalizedValue === null ||
                        normalizedValue === ""
                      )
                        return ""
                      if (typeof normalizedValue === "boolean")
                        return normalizedValue ? "true" : "false"
                      if (typeof normalizedValue === "number")
                        return String(normalizedValue)
                      if (Array.isArray(normalizedValue))
                        return JSON.stringify(normalizedValue)
                      if (typeof normalizedValue === "object")
                        return JSON.stringify(normalizedValue)
                      return String(normalizedValue)
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
      return (
        <tr key={label}>
          <th
            style={{
              textAlign: "left",
              paddingRight: "0.75rem",
              verticalAlign: "top",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </th>
          <td style={{ whiteSpace: isObject ? "normal" : "inherit" }}>
            {isObject ? renderStreamObjectTable(value) || "" : content}
          </td>
        </tr>
      )
    }

    const renderJsonDetails = (title, data) => {
      if (!data || typeof data !== "object") return null
      return (
        <details style={{ marginTop: "0.5rem" }}>
          <summary style={{ cursor: "pointer" }}>{title}</summary>
          <pre
            style={{
              margin: "0.5rem 0 0",
              backgroundColor: "rgba(15, 23, 42, 0.05)",
              padding: "0.75rem",
              borderRadius: "var(--border-radius)",
              overflowX: "auto",
            }}
          >
            {JSON.stringify(normalizeValue(data), null, 2)}
          </pre>
        </details>
      )
    }

    return (
      <div style={{ marginTop: "1.5rem" }}>
        <h3>Data Streams</h3>
        {dataStreams.map(({ stream }, idx) => {
          if (!stream || typeof stream !== "object") return null
          const streamName =
            typeof stream.name === "string" && stream.name
              ? stream.name
              : `stream-${idx}`
          const displayName =
            typeof stream.displayName === "string" && stream.displayName
              ? stream.displayName
              : streamName
          const streamId =
            typeof stream.name === "string"
              ? stream.name.split("/").pop()
              : null
          const type = typeof stream.type === "string" ? stream.type : null
          const webData =
            stream.webStreamData && typeof stream.webStreamData === "object"
              ? stream.webStreamData
              : null
          const iosData =
            stream.iosAppStreamData &&
            typeof stream.iosAppStreamData === "object"
              ? stream.iosAppStreamData
              : null
          const androidData =
            stream.androidAppStreamData &&
            typeof stream.androidAppStreamData === "object"
              ? stream.androidAppStreamData
              : null

          const defaultUri =
            typeof webData?.defaultUri === "string"
              ? webData.defaultUri.trim()
              : ""
          const audits = []
          if (!defaultUri) {
            audits.push({
              level: "error",
              message: "Default URI is not configured.",
            })
          } else if (!isValidHttpUrl(defaultUri)) {
            audits.push({
              level: "error",
              message:
                "Default URI must be a valid URL with http:// or https://.",
              value: defaultUri,
            })
          }

          return (
            <div
              key={streamName}
              style={{
                border: "1px solid var(--border-color, #d1d5db)",
                borderRadius: "var(--border-radius)",
                padding: "1rem",
                marginTop: idx === 0 ? "0.5rem" : "1rem",
                backgroundColor: "var(--background-color, #fff)",
              }}
            >
              <h4 style={{ margin: "0 0 0.5rem 0" }}>
                {displayName}
                {type && (
                  <span
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: "normal",
                      marginLeft: "0.5rem",
                      color: "#475569",
                    }}
                  >
                    ({type})
                  </span>
                )}
              </h4>
              <table className="ui-table">
                <tbody>
                  {renderInfoRow("Resource Name", stream.name)}
                  {renderInfoRow("Stream ID", streamId)}
                  {renderInfoRow("Display Name", stream.displayName)}
                  {renderInfoRow("Create Time", stream.createTime)}
                  {renderInfoRow("Update Time", stream.updateTime)}
                  {renderInfoRow(
                    "Measurement ID",
                    webData?.measurementId || stream.measurementId
                  )}
                  {renderInfoRow("Default URI", webData?.defaultUri)}
                  {renderInfoRow(
                    "Firebase App ID",
                    webData?.firebaseAppId ||
                      iosData?.firebaseAppId ||
                      androidData?.firebaseAppId
                  )}
                </tbody>
              </table>
              {renderJsonDetails("Web Stream Data", webData)}
              {renderJsonDetails("iOS App Stream Data", iosData)}
              {renderJsonDetails("Android App Stream Data", androidData)}
              {audits.length > 0 && (
                <div style={{ marginTop: "0.75rem" }}>
                  <strong>Audits</strong>
                  <ul
                    style={{
                      margin: "0.5rem 0 0 1.25rem",
                      color: "#b91c1c",
                    }}
                  >
                    {audits.map((audit, issueIdx) => (
                      <li key={issueIdx}>
                        <span>{audit.message}</span>
                        {audit.value && (
                          <div
                            style={{
                              marginTop: "0.25rem",
                              display: "inline-block",
                              backgroundColor: "rgba(185, 28, 28, 0.08)",
                              padding: "0.125rem 0.375rem",
                              borderRadius: "var(--border-radius)",
                              color: "#991b1b",
                            }}
                          >
                            <code
                              style={{
                                background: "transparent",
                                padding: 0,
                                fontSize: "0.85rem",
                              }}
                            >
                              {audit.value}
                            </code>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const renderCustomDefinitionsTable = () => {
    const rows = [
      ...(report.customDefinitionsMissing || []).map((d) => ({
        type: d.type,
        parameterName: d.parameterName,
        status: "Missing",
        expectedDisplayName: d.displayName || "",
        actualDisplayName: "",
      })),
      ...(report.customDefinitionsExtra || []).map((d) => ({
        type: d.type,
        parameterName: d.parameterName,
        status: "Extra",
        expectedDisplayName: "",
        actualDisplayName: d.displayName || "",
      })),
      ...(report.customDefinitionsDisplayNameMismatch || []).map((d) => ({
        type: d.type,
        parameterName: d.parameterName,
        status: "Display Name Mismatch",
        expectedDisplayName: d.expectedDisplayName || "",
        actualDisplayName: d.actualDisplayName || "",
      })),
    ]
    if (!rows.length) return null
    return (
      <div>
        <h3>Custom Definitions</h3>
        <table className="ui-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Type</th>
              <th>Parameter Name</th>
              <th>Expected Display Name</th>
              <th>Actual Display Name</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d, idx) => (
              <tr key={idx}>
                <td>{d.status}</td>
                <td>{d.type}</td>
                <td>{d.parameterName}</td>
                <td>{d.expectedDisplayName}</td>
                <td>{d.actualDisplayName}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderKeyEventsTable = () => {
    const rows = [
      ...(report.keyEventsMissing || []).map((e) => ({
        ...e,
        status: "Missing",
      })),
      ...(report.keyEventsExtra || []).map((e) => ({
        ...e,
        status: "Extra",
      })),
    ]
    if (!rows.length) return null
    return (
      <div>
        <h3>Key Events</h3>
        <table className="ui-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Event Name</th>
              <th>Counting Method</th>
              <th>Default Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e, idx) => (
              <tr key={idx}>
                <td>{e.status}</td>
                <td>{e.eventName}</td>
                <td>{e.countingMethod}</td>
                <td>{JSON.stringify(normalizeValue(e.defaultValue))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const BIGQUERY_FIELD_LABELS = {
    dailyExportEnabled: "Daily export",
    streamingExportEnabled: "Streaming export",
    freshDailyExportEnabled: "Fresh daily export",
    includeAdvertisingId: "Include advertising ID",
  }

  const formatBigQueryLinkDetails = (link) => {
    if (!link || typeof link !== "object") return ""
    const parts = []
    if (typeof link.project === "string" && link.project.trim()) {
      parts.push(`Project: ${link.project.trim()}`)
    }
    const appendBoolean = (field, label) => {
      if (link[field] === true) parts.push(`${label}: Enabled`)
      else if (link[field] === false) parts.push(`${label}: Disabled`)
    }
    appendBoolean("dailyExportEnabled", BIGQUERY_FIELD_LABELS.dailyExportEnabled)
    appendBoolean(
      "streamingExportEnabled",
      BIGQUERY_FIELD_LABELS.streamingExportEnabled
    )
    appendBoolean(
      "freshDailyExportEnabled",
      BIGQUERY_FIELD_LABELS.freshDailyExportEnabled
    )
    appendBoolean(
      "includeAdvertisingId",
      BIGQUERY_FIELD_LABELS.includeAdvertisingId
    )
    return parts.join("; ")
  }

  const formatMismatchDetails = (fields) => {
    if (!fields || typeof fields !== "object") return ""
    return Object.entries(fields)
      .map(([field, values]) => {
        if (!values || typeof values !== "object") return field
        const label = BIGQUERY_FIELD_LABELS[field] || field
        const expected =
          typeof values.expected === "boolean"
            ? values.expected
              ? "Enabled"
              : "Disabled"
            : values.expected ?? ""
        const actual =
          typeof values.actual === "boolean"
            ? values.actual
              ? "Enabled"
              : "Disabled"
            : values.actual ?? ""
        return `${label}: expected ${expected || "—"}, actual ${actual || "—"}`
      })
      .join("; ")
  }

  const renderBigQueryLinksTable = () => {
    const rows = []
    ;(report.bigQueryLinksMissing || []).forEach((entry) => {
      rows.push({
        status: "Missing",
        project:
          entry.project || entry.expected?.project || "",
        expected: entry.expected || {},
        actual: null,
        mismatchedFields: null,
      })
    })
    ;(report.bigQueryLinksMismatch || []).forEach((entry) => {
      rows.push({
        status: "Mismatch",
        project:
          entry.project ||
          entry.expected?.project ||
          entry.actual?.project ||
          "",
        expected: entry.expected || {},
        actual: entry.actual || {},
        mismatchedFields: entry.mismatchedFields || null,
      })
    })
    ;(report.bigQueryLinksExtra || []).forEach((entry) => {
      rows.push({
        status: "Extra",
        project: entry.project || "",
        expected: null,
        actual: entry,
        mismatchedFields: null,
      })
    })
    if (!rows.length) return null
    return (
      <div>
        <h3>BigQuery Links</h3>
        <table className="ui-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Project</th>
              <th>Expected</th>
              <th>Actual</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                <td>{row.status}</td>
                <td>{row.project}</td>
                <td>{formatBigQueryLinkDetails(row.expected)}</td>
                <td>{formatBigQueryLinkDetails(row.actual)}</td>
                <td>{formatMismatchDetails(row.mismatchedFields)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const MATCH_TYPE_LABELS = {
    MATCH_TYPE_UNSPECIFIED: "matches",
    EXACT: "equals",
    BEGINS_WITH: "begins with",
    ENDS_WITH: "ends with",
    CONTAINS: "contains",
    FULL_REGEXP: "matches regex (full)",
    PARTIAL_REGEXP: "matches regex (partial)",
  }

  const PARAMETER_OPERATION_SET = new Set(["SET_VALUE", "COPY_PARAMETER"])
  const PARAMETER_VALUE_TYPE_SET = new Set(["STRING", "INT", "DOUBLE", "BOOLEAN"])

  const describeMatchType = (matchType, caseSensitive) => {
    const upperType = typeof matchType === "string" ? matchType.toUpperCase() : "MATCH_TYPE_UNSPECIFIED"
    const label = MATCH_TYPE_LABELS[upperType] || upperType.toLowerCase()
    if (caseSensitive === false) return `${label} (ignore case)`
    return label
  }

  const formatMatchingConditions = (conditions) => {
    if (!Array.isArray(conditions) || !conditions.length) return "—"
    const entries = conditions
      .map((condition) => {
        const normalized = normalizeValue(condition)
        if (!normalized || typeof normalized !== "object") return null
        const eventParameter =
          typeof normalized.eventParameter === "string"
            ? normalized.eventParameter
            : typeof normalized.parameter === "string"
            ? normalized.parameter
            : ""
        if (!eventParameter) return null
        const matchType =
          typeof normalized.matchType === "string"
            ? normalized.matchType
            : typeof normalized.operator === "string"
            ? normalized.operator
            : "MATCH_TYPE_UNSPECIFIED"
        const caseSensitive =
          typeof normalized.caseSensitive === "boolean"
            ? normalized.caseSensitive
            : !(typeof normalized.ignoreCase === "boolean" && normalized.ignoreCase)
        const value =
          typeof normalized.value === "string"
            ? normalized.value
            : typeof normalized.comparisonValue === "string"
            ? normalized.comparisonValue
            : ""
        const negated = normalized.negated === true
        const label = describeMatchType(matchType, caseSensitive)
        const segment = value ? `${eventParameter} ${label} ${value}` : `${eventParameter} ${label}`
        return negated ? `NOT (${segment})` : segment
      })
      .filter(Boolean)
    return entries.length ? entries.join("; ") : "—"
  }

  const formatParameterMutations = (mutations) => {
    if (!Array.isArray(mutations) || !mutations.length) return "—"
    const entries = mutations
      .map((mutation) => {
        const normalized = normalizeValue(mutation)
        if (!normalized || typeof normalized !== "object") return null
        const parameter =
          typeof normalized.parameter === "string" ? normalized.parameter : ""
        if (!parameter) return null
        let operation =
          typeof normalized.operation === "string"
            ? normalized.operation.toUpperCase()
            : ""
        if (!PARAMETER_OPERATION_SET.has(operation)) {
          operation = normalized.copyFromParameter ? "COPY_PARAMETER" : "SET_VALUE"
        }
        if (operation === "COPY_PARAMETER") {
          const source =
            typeof normalized.copyFromParameter === "string"
              ? normalized.copyFromParameter
              : typeof normalized.sourceParameter === "string"
              ? normalized.sourceParameter
              : ""
          return `${parameter} ← ${source || "(copy parameter)"}`
        }
        let valueType =
          typeof normalized.valueType === "string"
            ? normalized.valueType.toUpperCase()
            : ""
        let value = normalized.value
        const parameterValue = normalizeValue(normalized.parameterValue)
        if (parameterValue && typeof parameterValue === "object") {
          if (parameterValue.stringValue != null) {
            valueType = "STRING"
            value = parameterValue.stringValue
          } else if (parameterValue.doubleValue != null) {
            valueType = "DOUBLE"
            value = parameterValue.doubleValue
          } else if (parameterValue.intValue != null) {
            valueType = "INT"
            value = parameterValue.intValue
          } else if (typeof parameterValue.boolValue === "boolean") {
            valueType = "BOOLEAN"
            value = parameterValue.boolValue ? "true" : "false"
          }
        }
        if (!PARAMETER_VALUE_TYPE_SET.has(valueType)) valueType = "STRING"
        if (value == null && typeof normalized.copyFromParameter === "string") {
          return `${parameter} ← ${normalized.copyFromParameter}`
        }
        if (value == null) value = ""
        if (typeof value !== "string") value = String(value)
        const typeLabel = valueType.toLowerCase()
        const displayValue = value === "" ? "(empty)" : value
        return `${parameter} = ${displayValue} (${typeLabel})`
      })
      .filter(Boolean)
    return entries.length ? entries.join("; ") : "—"
  }

  const renderEventCreateRulesTable = () => {
    const rows = [
      ...(report.eventCreateRulesMissing || []).map((rule) => ({
        ...rule,
        status: "Missing",
      })),
      ...(report.eventCreateRulesExtra || []).map((rule) => ({
        ...rule,
        status: "Extra",
      })),
    ]
    if (!rows.length) return null
    return (
      <div>
        <h3>Custom Event Create Rules</h3>
        <table className="ui-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Source Event</th>
              <th>Destination Event</th>
              <th>Matching Conditions</th>
              <th>Parameter Updates</th>
              <th>Copy Parameters</th>
              <th>Disabled</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((rule, idx) => (
              <tr key={idx}>
                <td>{rule.status}</td>
                <td>{rule.sourceEvent || ""}</td>
                <td>{rule.destinationEvent || ""}</td>
                <td>{formatMatchingConditions(rule.matchingConditions)}</td>
                <td>{formatParameterMutations(rule.parameterMutations)}</td>
                <td>{rule.copyParameters ? "Yes" : "No"}</td>
                <td>{rule.disabled ? "Yes" : "No"}</td>
                <td>{rule.description || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const renderEventEditRulesTable = () => {
    const rows = [
      ...(report.eventEditRulesMissing || []).map((rule) => ({
        ...rule,
        status: "Missing",
      })),
      ...(report.eventEditRulesExtra || []).map((rule) => ({
        ...rule,
        status: "Extra",
      })),
    ]
    if (!rows.length) return null
    return (
      <div>
        <h3>Custom Event Modify Rules</h3>
        <table className="ui-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Event Name</th>
              <th>Matching Conditions</th>
              <th>Parameter Updates</th>
              <th>Disabled</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((rule, idx) => (
              <tr key={idx}>
                <td>{rule.status}</td>
                <td>{rule.eventName || ""}</td>
                <td>{formatMatchingConditions(rule.matchingConditions)}</td>
                <td>{formatParameterMutations(rule.parameterMutations)}</td>
                <td>{rule.disabled ? "Yes" : "No"}</td>
                <td>{rule.description || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <div className="page-container">
      <h2>Property Settings</h2>
      <button onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId)}`)}>
        Back
      </button>
      {loading ? (
        <p>Loading...</p>
      ) : propertyError ? (
        <p style={{ marginTop: "1rem", color: "#b00020" }}>
          Failed to load property details.
        </p>
      ) : !propInfo ? (
        <p style={{ marginTop: "1rem" }}>Property not found.</p>
      ) : (
        <>
          <div style={{ marginTop: "1rem" }}>
            <h3 style={{ margin: 0 }}>
              {propInfo.property_name}
              <span
                style={{
                  fontWeight: "normal",
                  fontSize: "0.9em",
                  marginLeft: "0.5rem",
                }}
              >
                ({propInfo.property_id})
              </span>
            </h3>
          </div>
          {reportError && (
            <p style={{ marginTop: "1rem", color: "#b00020" }}>{reportError}</p>
          )}
          {error && !editing && (
            <p style={{ marginTop: "1rem", color: "#b00020" }}>{error}</p>
          )}
          <div style={{ marginTop: "1rem" }}>
            {!editing && (
              <button
                style={{ marginBottom: "0.5rem" }}
                onClick={startAdjust}
              >
                Adjust GA4
              </button>
            )}
            {renderSettingsTable()}
            {renderEnhancedMeasurementSection()}
            {renderDataStreamsSection()}
            {renderCustomDefinitionsTable()}
            {renderKeyEventsTable()}
            {renderBigQueryLinksTable()}
            {renderEventCreateRulesTable()}
            {renderEventEditRulesTable()}
          </div>
        </>
      )}
    </div>
  )
}
